import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [billing, setBilling]     = useState(null);
  const [billingLoaded, setBillingLoaded] = useState(false);

  const fetchBilling = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setBillingLoaded(true); return; }
    try {
      const res = await fetch('/api/billing/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBilling(data);
      }
    } catch {}
    setBillingLoaded(true);
  }, []);

  useEffect(() => {
    if (user) fetchBilling();
    else setBillingLoaded(true);
  }, [user]);

  const login = useCallback((token, landlord) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(landlord));
    setUser(landlord);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setBilling(null);
    setBillingLoaded(false);
  }, []);

  const refreshUser = useCallback(() => {
    const stored = localStorage.getItem('user');
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const refreshBilling = useCallback(async () => {
    await fetchBilling();
  }, [fetchBilling]);

  // billingReady — true if user has an active/trialing Stripe sub
  // OR if they signed up before Stripe billing was introduced (14 Apr 2026)
  const BILLING_CUTOFF = new Date('2026-04-13');
  const userCreatedAt  = user?.created_at ? new Date(user.created_at) : null;
  const isLegacyUser   = userCreatedAt && userCreatedAt < BILLING_CUTOFF;

  // billingReady — must have Stripe sub (stripe_sub_id present)
  // 'trialing' without stripe_sub_id means old DB trial, not Stripe trial
  const hasStripeSub = billing?.stripe_sub_id != null && billing?.stripe_sub_id !== '';
  const billingReady = isLegacyUser ||
                       billing?.sub_status === 'active' ||
                       (billing?.sub_status === 'trialing' && hasStripeSub);

  return (
    <AuthContext.Provider value={{
      user, login, logout, refreshUser,
      billing, billingLoaded, billingReady, refreshBilling,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);